import { createContext, useContext } from "react";
import { TAuthStoreState, useStore } from "../store";

import { tokenStorage } from "../utils/tokenStorage";

export type TAuthContext = TAuthStoreState | null;

const AuthContext = createContext<TAuthContext | null>(null);

/**
 * AuthProvider component that provides auth state to the app.
 * Token refresh is now handled reactively in apiService.ts on 401 responses.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const user = useStore.use.user();
  const profile = useStore.use.profile();
  const roles = useStore.use.roles();
  const resetToken = useStore.use.resetToken();
  // Read tokens from cookies as single source of truth
  const accessToken = tokenStorage.getAccessToken();
  const expiresAt = tokenStorage.getExpiresAt();
  const isRefreshing = useStore((state) => state.isRefreshing);
  const isProfileLoading = useStore((state) => state.isProfileLoading);
  const profileError = useStore((state) => state.profileError);
  const isUpdatingProfile = useStore((state) => state.isUpdatingProfile);
  const updateProfileError = useStore((state) => state.updateProfileError);
  const isSendingVerificationOtp = useStore((state) => state.isSendingVerificationOtp);
  const isResendingVerificationOtp = useStore((state) => state.isResendingVerificationOtp);
  const isVerifyingVerificationOtp = useStore((state) => state.isVerifyingVerificationOtp);
  const verificationOtpMethod = useStore((state) => state.verificationOtpMethod);
  const verificationOtpSentAt = useStore((state) => state.verificationOtpSentAt);
  const verificationOtpExpiresAt = useStore((state) => state.verificationOtpExpiresAt);
  const verificationOtpResendAvailableAt = useStore((state) => state.verificationOtpResendAvailableAt);
  const verificationOtpFailedAttempts = useStore((state) => state.verificationOtpFailedAttempts);
  const isRolesLoading = useStore((state) => state.isRolesLoading);
  const rolesError = useStore((state) => state.rolesError);
  const isChangingVerificationContact = useStore((state) => state.isChangingVerificationContact);
  const verificationRequestId = useStore((state) => state.verificationRequestId);
  const pendingEmailChange = useStore((state) => state.pendingEmailChange);

  const authContextValue: TAuthStoreState = {
    user,
    profile: profile ?? null,
    roles: roles ?? null,
    resetToken: resetToken ?? null,
    accessToken,
    expiresAt,
    isRefreshing,
    isProfileLoading,
    profileError,
    isRolesLoading,
    rolesError,
    isUpdatingProfile,
    updateProfileError,
    isSendingVerificationOtp,
    isResendingVerificationOtp,
    isVerifyingVerificationOtp,
    isChangingVerificationContact,
    verificationOtpMethod,
    verificationOtpSentAt,
    verificationOtpExpiresAt,
    verificationOtpResendAvailableAt,
    verificationOtpFailedAttempts,
    verificationRequestId,
    pendingEmailChange,
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
